import { encryptAsymmetric } from '@uniqkey-frontend/shared-app';
import {
  type VaultCreditCardCreationRequestedForEmployeePayload,
} from '@uniqkey-backend-organization-web/api-client';
import type { ITrustedPortalHandlerParams } from '../../../interfaces';
import APIClientsProvider from '../../../apiClientsProvider';
import handleAdminVaultCreditCardCreation from '../common/handleAdminVaultCreditCardCreation';

const ERROR_PREFIX = 'TrustedPortal/handleAdminVaultCreditCardCreationForEmployeeRequestedEvent';

const handleAdminVaultCreditCardCreationForEmployeeRequestedEvent = async (
  params: ITrustedPortalHandlerParams<VaultCreditCardCreationRequestedForEmployeePayload>,
) => {
  const {
    parsedEvent,
    axiosInstance,
    organizationPublicKey,
  } = params;

  const { parsedPayload, queueMessageId } = parsedEvent;
  const {
    expiryDate,
    cardNumber,
    cvv,
    cardHolderName,
    employeeAccountId,
    employeeAccountKeyPairPublic,
  } = parsedPayload;

  if (!employeeAccountId) {
    throw new Error(`${ERROR_PREFIX} no employeeAccountId in event`);
  }

  if (!cardNumber) {
    throw new Error(`${ERROR_PREFIX} no cardNumber in event`);
  }

  if (!employeeAccountKeyPairPublic) {
    throw new Error(`${ERROR_PREFIX} no employeeAccountKeyPairPublic in event`);
  }

  const {
    encryptedCardNumber,
    encryptedCardHolderName,
    encryptedExpiryDate,
    encryptedCVV,
    creditCardInfo,
    symmetricKey,
    encryptedSymmetricKeyBackup,
  } = await handleAdminVaultCreditCardCreation({
    organizationPublicKey,
    cardNumber,
    cardHolderName,
    expiryDate,
    cvv,
    errorPrefix: ERROR_PREFIX,
  });

  const encryptedSymmetricKey = await encryptAsymmetric({
    string: symmetricKey,
    publicKey: employeeAccountKeyPairPublic,
  });

  await APIClientsProvider.Mobile.getQueueAPIClient(axiosInstance)
    .apiV1QueueAdminVaultCreditCardCreationRequestedForEmployeeApprovePost({
      queueMessageId,
      cardNumber: encryptedCardNumber,
      cardHolderName: encryptedCardHolderName,
      expiryDate: encryptedExpiryDate,
      cvv: encryptedCVV,
      lastDigits: creditCardInfo.lastDigits,
      paymentSystem: creditCardInfo.paymentSystem,
      vaultSymmetricKey: encryptedSymmetricKey,
      vaultSymmetricKeyBackup: encryptedSymmetricKeyBackup,
    });
};

export default handleAdminVaultCreditCardCreationForEmployeeRequestedEvent;
